import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { u2000BuildAppRouteHref } from "../../../../../../u2000Core/tools/u2000OpenAppRouteInNewTab";
import U2000BasicDialog from "../../../../../../u2000Core/components/dialog/U2000BasicDialog";
import U2000DetailTable from "../../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import { useU2000GridStateHandlers } from "../../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U2000TableColumn } from "../../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import { u3125FormatCurrency } from "../../../../../../u3125Facture/U3125";
import { u3125FormatBillingSourceDisplayAmount } from "../../../../../../u3125Facture/U3125Service";
import { NS } from "../../../../../U3121";
import type { U3121BillingDocumentNature, U3121BillingInvoiceDetailLine } from "../../../../../U3121Service";
import { useU3121BillingSummaryInvoiceDetailHandlers } from "../../../../../U3121UseBillingSummaryHandlers";
import U3121BillingSummaryDetailDlgBody from "./U3121BillingSummaryDetailDlgBody";
import { u3121InitBillingSummaryDetailGrid } from "./u3121InitBillingSummaryDetailGrid";
import { u3121BillingSummaryHeaderDisplayIfTwoLine } from "./u3121BillingSummaryTwoLineHeader";

export interface U3121BillingSummaryInvoiceDetailDlgProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  documentNature: U3121BillingDocumentNature;
}

function dlgTitleKey(nature: U3121BillingDocumentNature): string {
  if (nature === "CREDIT") return "dlgBillingSummaryCreditTitle";
  if (nature === "ADVANCE") return "dlgBillingSummaryAdvanceTitle";
  if (nature === "VOID") return "dlgBillingSummaryVoidTitle";
  return "dlgBillingSummaryStandardTitle";
}

function formatDate(value: unknown): string {
  if (value == null || value === "") return "";
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function formatCreditDetailAmount(value: unknown): string {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return u3125FormatCurrency(u3125FormatBillingSourceDisplayAmount(num, "CREDIT"));
}

/** Largeur colonne : % en mode fit ; min-width en grille CREDIT (scroll horizontal). */
function billingInvoiceDetailWidthClass(
  nature: U3121BillingDocumentNature,
  fitClass: string,
  scrollMinClass = "min-w-28 shrink-0",
): string {
  return nature === "CREDIT" ? scrollMinClass : fitClass;
}

function creditVentilatedAmountColumns(t: TFunction, twoLineHeader: typeof u3121BillingSummaryHeaderDisplayIfTwoLine): U2000TableColumn[] {
  const col = (name: string, labelKey: string): U2000TableColumn => ({
    name,
    label: t(labelKey, { ns: NS }),
    headerDisplay: twoLineHeader,
    inGridField: true,
    sortable: true,
    isDefaultColumn: true,
    widthClass: "min-w-28 shrink-0",
    type: "number",
    align: "right",
    display: (_c, value) => formatCreditDetailAmount(value),
  });
  return [
    col("originalAmountTaxable", "colCreditOriginalAmountTaxable"),
    col("originalAmountNonTaxable", "colCreditOriginalAmountNonTaxable"),
    col("appliedAmountTaxable", "colCreditAppliedAmountTaxable"),
    col("appliedAmountNonTaxable", "colCreditAppliedAmountNonTaxable"),
    col("remainingAmountTaxable", "colCreditRemainingAmountTaxable"),
    col("remainingAmountNonTaxable", "colCreditRemainingAmountNonTaxable"),
  ];
}

function getColumnsDef(nature: U3121BillingDocumentNature, t: TFunction, language: string): U2000TableColumn[] {
  const twoLineHeader = u3121BillingSummaryHeaderDisplayIfTwoLine;
  const base: U2000TableColumn[] = [
    {
      name: "invoiceNumber",
      label: t("colBillingSummaryInvoiceNumber", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: billingInvoiceDetailWidthClass(nature, "w-[9%]", "min-w-22 shrink-0"),
      type: "string",
      align: "left",
      display: (_c, value, row) => {
        const id = (row as U3121BillingInvoiceDetailLine).id;
        if (!id) return value == null ? "—" : String(value);
        return (
          <a
            href={u2000BuildAppRouteHref(`/U3125/${id}`)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {value == null ? id : String(value)}
          </a>
        );
      },
    },
    {
      name: "invoiceDate",
      label: t("colBillingSummaryInvoiceDate", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: billingInvoiceDetailWidthClass(nature, "w-[8%]", "min-w-22 shrink-0"),
      type: "string",
      align: "left",
      display: (_c, value) => formatDate(value),
    },
    {
      name: "status",
      label: t("colBillingSummaryInvoiceStatus", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: billingInvoiceDetailWidthClass(nature, "w-[15%]", "min-w-36 shrink-0"),
      type: "string",
      align: "left",
      display: (_c, value, row) => {
        const status = String((row as U3121BillingInvoiceDetailLine).status ?? value ?? "");
        return status ? t(`lblInvoiceStatus_${status}`, { ns: NS, defaultValue: status }) : "—";
      },
    },
    {
      name: "invoiceTypeCode",
      label: t("colBillingSummaryInvoiceType", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: billingInvoiceDetailWidthClass(nature, "w-[12%]", "min-w-40 shrink-0"),
      type: "string",
      align: "left",
      display: (_c, value, row) => {
        const line = row as U3121BillingInvoiceDetailLine;
        const code = String(line.invoiceTypeCode ?? value ?? "").trim();
        if (!code) return "—";
        const label = language.startsWith("en")
          ? String(line.invoiceTypeLabelEn ?? "").trim()
          : String(line.invoiceTypeLabelFr ?? "").trim();
        const displayValue = !label || label === code ? code : `${label} (${code})`;
        return <span title={displayValue}>{displayValue}</span>;
      },
    },
  ];

  if (nature === "VOID") {
    return [
      ...base,
      {
        name: "documentNature",
        label: t("colBillingSummaryDocumentNature", { ns: NS }),
        inGridField: true,
        sortable: true,
        isDefaultColumn: true,
        widthClass: "w-[12%]",
        type: "string",
        align: "left",
        display: (_c, value, row) => {
          const docNature = (row as U3121BillingInvoiceDetailLine).documentNature ?? value;
          if (docNature == null || docNature === "") return "—";
          return t(`lblBillingInvoiceRowNature_${String(docNature)}`, { ns: NS, defaultValue: String(docNature) });
        },
      },
      {
        name: "invoiceTotal",
        label: t("colBillingSummaryVoidAmount", { ns: NS }),
        inGridField: true,
        sortable: true,
        isDefaultColumn: true,
        widthClass: "w-[12%]",
        type: "number",
        align: "right",
        display: (_c, value, row) => {
          const line = row as U3121BillingInvoiceDetailLine;
          const num = Number(value) || 0;
          const display =
            line.documentNature === "CREDIT" ? u3125FormatBillingSourceDisplayAmount(num, "CREDIT") : num;
          return u3125FormatCurrency(display);
        },
      },
    ];
  }

  if (nature === "STANDARD") {
    return [
      ...base,
      {
        name: "amountTec",
        label: t("colBillingSummaryAmountTec", { ns: NS }),
        inGridField: true,
        sortable: true,
        isDefaultColumn: true,
        widthClass: "w-[8%]",
        type: "number",
        align: "right",
        display: (_c, value) => u3125FormatCurrency(value),
      },
      {
        name: "grossAmountBeforeCredit",
        label: t("colGrossBeforeCredit", { ns: NS }),
        headerDisplay: twoLineHeader,
        inGridField: true,
        sortable: true,
        isDefaultColumn: true,
        widthClass: "w-[11%]",
        type: "number",
        align: "right",
        display: (_c, _value, row) => {
          const line = row as U3121BillingInvoiceDetailLine;
          const taxable = Number(line.grossAmountInvoiceTaxable ?? 0);
          const nonTaxable = Number(line.grossAmountInvoiceNonTaxable ?? 0);
          return u3125FormatCurrency(taxable + nonTaxable);
        },
      },
      {
        name: "invoiceTotal",
        label: t("colBillingSummaryInvoiceTotal", { ns: NS }),
        inGridField: true,
        sortable: true,
        isDefaultColumn: true,
        widthClass: "w-[8%]",
        type: "number",
        align: "right",
        display: (_c, value) => u3125FormatCurrency(value),
      },
      {
        name: "paymentsReceived",
        label: t("colBillingSummaryPaymentsReceived", { ns: NS }),
        inGridField: true,
        sortable: true,
        isDefaultColumn: true,
        widthClass: "w-[8%]",
        type: "number",
        align: "right",
        display: (_c, value) => u3125FormatCurrency(value),
      },
      {
        name: "balanceDue",
        label: t("colBillingSummaryBalanceDue", { ns: NS }),
        inGridField: true,
        sortable: true,
        isDefaultColumn: true,
        widthClass: "w-[8%]",
        type: "number",
        align: "right",
        display: (_c, value) => u3125FormatCurrency(value),
      },
    ];
  }

  const creditNature = nature === "CREDIT";
  const creditOrAdvanceAmountColumns: U2000TableColumn[] = [
    {
      name: "originalAmount",
      label: creditNature ? t("lblCreditAmount", { ns: NS }) : t("lblTotalOriginalAmount", { ns: NS }),
      headerDisplay: twoLineHeader,
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: billingInvoiceDetailWidthClass(nature, "w-[8%]"),
      type: "number",
      align: "right",
      display: (_c, value) =>
        u3125FormatCurrency(u3125FormatBillingSourceDisplayAmount(value as number, nature)),
    },
    {
      name: "appliedAmount",
      label: t("lblTotalAppliedAmount", { ns: NS }),
      headerDisplay: twoLineHeader,
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: billingInvoiceDetailWidthClass(nature, "w-[8%]"),
      type: "number",
      align: "right",
      display: (_c, value) =>
        u3125FormatCurrency(creditNature ? u3125FormatBillingSourceDisplayAmount(value as number, "CREDIT") : value),
    },
    {
      name: "remainingAmount",
      label: t("lblTotalRemainingAmount", { ns: NS }),
      headerDisplay: twoLineHeader,
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: billingInvoiceDetailWidthClass(nature, "w-[8%]"),
      type: "number",
      align: "right",
      display: (_c, value) =>
        u3125FormatCurrency(creditNature ? u3125FormatBillingSourceDisplayAmount(value as number, "CREDIT") : value),
    },
  ];

  return [
    ...base,
    ...creditOrAdvanceAmountColumns,
    ...(creditNature ? creditVentilatedAmountColumns(t, twoLineHeader) : []),
    {
      name: "consumptionsSummary",
      label: t("colBillingSummaryConsumptions", { ns: NS }),
      inGridField: false,
      sortable: false,
      isDefaultColumn: true,
      widthClass: billingInvoiceDetailWidthClass(nature, "w-[20%]", "min-w-56 shrink-0"),
      type: "string",
      align: "left",
      display: (_c, _v, row) => {
        const line = row as U3121BillingInvoiceDetailLine;
        const parts = (line.consumptions ?? []).map((c) => {
          const num = c.targetInvoiceNumber != null ? String(c.targetInvoiceNumber) : c.targetInvoiceId ?? "—";
          const date = formatDate(c.targetInvoiceDate);
          const datePart = date ? ` (${date})` : "";
          return `${num}${datePart}: ${u3125FormatCurrency(c.amount)}`;
        });
        return parts.length ? parts.join("; ") : "—";
      },
    },
    {
      name: "fullyConsumed",
      label: t("colBillingSummaryFullyConsumed", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: billingInvoiceDetailWidthClass(nature, "w-[8%]", "min-w-20 shrink-0"),
      type: "string",
      align: "center",
      display: (_c, value) => (value === true ? t("lblYes", { ns: NS }) : value === false ? t("lblNo", { ns: NS }) : "—"),
    },
  ];
}

const U3121BillingSummaryInvoiceDetailDlg: React.FC<U3121BillingSummaryInvoiceDetailDlgProps> = ({
  isOpen,
  onClose,
  projectId,
  documentNature,
}) => {
  const { t, i18n } = useTranslation();
  const gridStateHandlers = useU2000GridStateHandlers();
  const [loading, setLoading] = useState(false);
  const detailHandlers = useU3121BillingSummaryInvoiceDetailHandlers();

  useEffect(() => {
    if (!isOpen || !projectId.trim()) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const list = await detailHandlers.fetch(projectId, documentNature);
      if (cancelled) return;
      const columns = getColumnsDef(documentNature, t, i18n.language);
      u3121InitBillingSummaryDetailGrid(gridStateHandlers, columns, list);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- colonnes à l’ouverture
  }, [isOpen, projectId, documentNature, detailHandlers, t, i18n.language]);

  const title = useMemo(() => t(dlgTitleKey(documentNature), { ns: NS }), [documentNature, t]);

  return (
    <U2000BasicDialog
      isOpen={isOpen}
      onClose={() => onClose()}
      title={title}
      width={documentNature === "CREDIT" || documentNature === "ADVANCE" ? "w-[72vw]" : "w-[60vw]"}
      height="max-h-[60vh]"
    >
      <U3121BillingSummaryDetailDlgBody onClose={onClose}>
        {loading ? (
          <p className="text-sm text-gray-600">{t("msgLoading", { ns: NS })}</p>
        ) : gridStateHandlers.getTotalRows() === 0 ? (
          <p className="text-sm text-gray-600">{t("msgBillingSummaryDetailEmpty", { ns: NS })}</p>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-auto">
            <U2000DetailTable
              gridStateHandlers={gridStateHandlers}
              allowRowSelection={false}
              enableHorizontalScroll={documentNature === "CREDIT"}
              label={t("lblBillingSummaryDetailCount", { count: gridStateHandlers.getTotalRows(), ns: NS })}
            />
          </div>
        )}
      </U3121BillingSummaryDetailDlgBody>
    </U2000BasicDialog>
  );
};

export default U3121BillingSummaryInvoiceDetailDlg;
